import {forwardRef, ReactNode, useState, useLayoutEffect} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {Box, IconButton, Slide, SlideProps} from '@mui/material';
import {useCompany} from '@/shared/contexts/UserContext/useUserContext';
import ExitConfirmationModal from './ExitConfirmationModal';
import Modal from './Modal';

const Transition = forwardRef<HTMLDivElement, SlideProps>(function Transition(
  props: SlideProps,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function FullScreenModal({
  children,
  isOpened,
  onClose,
  title,
  id
}: {
  children: ReactNode;
  isOpened: boolean;
  onClose: () => void | null;
  title: ReactNode;
  id?: string;
}) {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const {isForClinicalTrials} = useCompany();

  const handleCloseClick = () => {
    // If not a clinical trial, close immediately without confirmation
    if (!isForClinicalTrials) {
      onClose();
      return;
    }

    // Blur the currently focused element to prevent aria-hidden warnings
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setShowConfirmation(true);
  };

  const handleConfirmClose = () => {
    setShowConfirmation(false);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleCancelClose = () => {
    setShowConfirmation(false);
  };

  useLayoutEffect(function () {
    const body = document.body;

    const observer = new MutationObserver(function () {
      // body.style.touchAction = body.style.overflow === 'hidden' ? 'none' : '';
      const root = document.getElementById('root');
      if (root) {
        root.style.overflow = body.style.overflow;
        root.style.touchAction = body.style.overflow === 'hidden' ? 'none' : '';
      }
    });

    observer.observe(body, {
      attributes: true,
      attributeFilter: ['style']
    });

    return function () {
      if (observer) {
        observer.disconnect();
      }
      const root = document.getElementById('root');
      if (root) {
        root.style.overflow = body.style.overflow;
        root.style.touchAction = '';
      }
    };
  }, []);

  return (
    <>
      <Modal
        fullScreen
        Transition={Transition}
        isOpened={isOpened}
        onClose={handleCloseClick}
        id={id}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
      >
        <>
          {title}
          <IconButton
            aria-label="close"
            onClick={handleCloseClick}
            data-testid="closeModalBtn"
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500]
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              p: 2
            }}
          >
            {children}
          </Box>
        </>
      </Modal>

      {isForClinicalTrials && (
        <ExitConfirmationModal
          open={showConfirmation}
          onConfirm={handleConfirmClose}
          onCancel={handleCancelClose}
        />
      )}
    </>
  );
}
